import { Modal } from 'antd'
import CantileverButton from 'components/CantileverButton'
import {
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import { SimpleFormField } from 'components/form/formTypes'
import { DetectionModelForm } from 'features/anomalyDetection/components/DetectionModelForm'
import { EditDetectionConfigurationDataContext } from 'features/anomalyDetection/contexts/EditDetectionConfigurationDataLayer'
import { DetectionModelDataFromFormFields } from 'features/anomalyDetection/forms/detectionModelFormFields'
import React, { useContext, useEffect, useRef } from 'react'
import { SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'

const FormContainer = styled.div`
  flex-direction: column;
  padding-top: 1rem;
  gap: 0;
`

interface EditDetectionModelModalProps {
  isOpen: boolean
  onClose: () => void
  editingModelIndex: number | null
}

export const EditDetectionModelModal: React.FC<
  EditDetectionModelModalProps
> = ({ isOpen, onClose, editingModelIndex }) => {
  const {
    detectionModelFormFields,
    setDetectionModelData,
    detectionConfiguration: { detection_models: detectionModels },
    setDetectionConfiguration,
  } = useContext(EditDetectionConfigurationDataContext)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (!isOpen) return
    if (editingModelIndex !== null && detectionModels.length > 0) {
      const model = detectionModels[editingModelIndex]
      setDetectionModelData((prev) => ({
        ...prev,
        modelName: model.model_name,
        isEnabled: model.is_enabled ? 'yes' : 'no',
        lowerThreshold: model.model_parameters.lower_threshold,
        lowerLowerThreshold: model.model_parameters.lower_lower_threshold,
        upperThreshold: model.model_parameters.upper_threshold,
        upperUpperThreshold: model.model_parameters.upper_upper_threshold,
      }))
    } else if (editingModelIndex === null) {
      setDetectionModelData({
        modelName: '',
        isEnabled: 'yes',
      })
    }
  }, [isOpen, editingModelIndex, detectionModels, setDetectionModelData])

  const handleSave = () => {
    if (formRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      formRef.current.dispatchEvent(event)
    }
  }

  const onSubmit: SubmitHandler<SimpleFormField> = (data) => {
    const convertedFormFields = createEditedObject(
      data,
      createUniqueFormFields(detectionModelFormFields)
    )
    const modelData = DetectionModelDataFromFormFields(
      convertedFormFields[0].formFields
    )

    const newModels = [...detectionModels]
    if (editingModelIndex !== null) {
      newModels[editingModelIndex] = modelData
    } else {
      newModels.push(modelData)
    }
    setDetectionConfiguration((prev) => ({
      ...prev,
      detection_models: newModels,
    }))
    onClose()
  }

  return (
    <Modal
      key={`detection-model-modal-${isOpen ? 'open' : 'closed'}`}
      title={
        editingModelIndex !== null
          ? 'Edit Detection Model'
          : 'Add Detection Model'
      }
      open={isOpen}
      width='50%'
      onCancel={onClose}
      footer={
        <CantileverButton primary onClick={handleSave}>
          {editingModelIndex !== null ? 'Update Model' : 'Save Model'}
        </CantileverButton>
      }>
      <FormContainer>
        <DetectionModelForm
          formFields={detectionModelFormFields}
          inEditMode={true}
          ref={formRef}
          onSubmit={onSubmit}
        />
      </FormContainer>
    </Modal>
  )
}
